import { Injectable } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { filter, fromEvent, map, merge, of, switchMap } from 'rxjs';
import { MainComponentActions } from '../main.actions';
import { WindowMessagesActions } from './window-messages.actions';
import { selectWindowStateState } from '../window/window.reducer';
import {
  selectIsConnected,
  selectOperatorName,
  selectReceivedFirstOperatorMessage,
} from '../live-chat/live-chat.reducer';
import { selectUrlParams } from './window-messages.reducer';
import { SettingsActions } from '../settings/settings.action';
import { WindowStateActions } from '../window/window.actions';
import { OverlayActions } from '../overlay-state/overlay.actions';
import { TranslateService } from '@ngx-translate/core';
import { PageListService } from '../../services/page-list.service';
import { VaButtonActions } from '../va-button/va-button.actions';
import { getUrlParams } from '../../../utils/getUrlParams';
import { selectPendingActionState } from '../pending-action/pending-action.reducer';
import { PendingActionActions } from '../pending-action/pending-action.actions';
import { urlChanges$ } from '../../../utils/urlChanges';
import {
  ChatWindowStates,
  OverlayType,
  PendingActionType,
  WindowMessage,
} from '../../types/enum';
import { ProactiveChatActions } from '../proactive-chat/proactive-chat.actions';
import { SafeSendActions } from '../safe-send/safe-send.actions';

@Injectable({
  providedIn: 'root',
})
export class WindowMessagesEffects {
  private receiveMessage$ = fromEvent<MessageEvent>(window, 'message');

  constructor(
    private actions$: Actions,
    private store: Store,
    private translateService: TranslateService,
    private pageListService: PageListService
  ) {}

  onReceiveSafeSendCloseMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MainComponentActions.mainComponentLoaded),
      switchMap(() => this.receiveMessage$),
      filter((event: MessageEvent) => event?.data == WindowMessage.SS_CLOSE),
      switchMap(() =>
        merge(
          of(
            SafeSendActions.createConfirmationMessage(),
            OverlayActions.closeOverlay()
          )
        )
      )
    )
  );

  onurlChangesInitializeApp$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MainComponentActions.mainComponentLoaded),
      switchMap(() => urlChanges$),
      map(() => {
        return WindowMessagesActions.initializeApp({
          urlParams: getUrlParams(),
        });
      })
    )
  );

  onReceiveChangeLanguageAppMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MainComponentActions.mainComponentLoaded),
      switchMap(() => this.receiveMessage$),
      filter(
        (event: MessageEvent) =>
          event?.data?.message == WindowMessage.UCX_CHANGE_LANGUAGE ||
          event?.data?.messageData?.language
      ),
      switchMap((event: MessageEvent) =>
        of(
          SettingsActions.setLanguage({
            value: event.data.messageData.language,
          })
        )
      )
    )
  );

  onReceiveOpenMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MainComponentActions.mainComponentLoaded),
      switchMap(() => this.receiveMessage$),
      filter(
        (event: MessageEvent) => event?.data?.message == WindowMessage.OPEN
      ),
      switchMap((event) =>
        of(
          WindowStateActions.requestToOpenWindow({
            context: event?.data?.messageData?.context,
          })
        )
      )
    )
  );

  onReceiveMaximizeMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MainComponentActions.mainComponentLoaded),
      switchMap(() => this.receiveMessage$),
      filter(
        (event: MessageEvent) => event?.data?.message == WindowMessage.MAXIMIZE
      ),
      switchMap(() => of(WindowStateActions.openWindow()))
    )
  );

  onReceivePopOverCloseMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MainComponentActions.mainComponentLoaded),
      switchMap(() => this.receiveMessage$),
      filter(
        (event: MessageEvent) =>
          event?.data?.message == WindowMessage.POPOVER_CLOSE
      ),
      switchMap(() => of(ProactiveChatActions.hideProactive()))
    )
  );

  onReceiveCloseRequestedMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MainComponentActions.mainComponentLoaded),
      switchMap(() => this.receiveMessage$),
      filter(
        (event: MessageEvent) =>
          event?.data?.message == WindowMessage.CLOSE_REQUESTED
      ),
      switchMap(() =>
        of(
          WindowStateActions.openWindow(),
          OverlayActions.showOverlay({ value: OverlayType.Confirmation })
        )
      )
    )
  );

  getPageList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WindowMessagesActions.initializeApp),
      switchMap(() => this.pageListService.loadPageList()),
      switchMap((pageList: string[]) => {
        return of(
          WindowMessagesActions.toggleFloatingBtn({
            pageList,
          })
        );
      })
    )
  );

  setLanguage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WindowMessagesActions.initializeApp),
      concatLatestFrom(() => this.store.select(selectUrlParams)),
      filter(([, urlParams]) => !!urlParams?.language),
      map(([, urlParams]) =>
        SettingsActions.setLanguage({ value: urlParams.language })
      )
    )
  );

  requestToOpenWindow$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WindowMessagesActions.initializeApp),
      concatLatestFrom(() => [
        this.store.select(selectUrlParams),
        this.store.select(selectIsConnected),
        this.store.select(selectWindowStateState),
      ]),
      map(([, urlParams, isLiveChatConnected, windowState]) => {
        if (urlParams?.vaJourney) {
          if (isLiveChatConnected) {
            // Keep the active session and open the window
            return WindowStateActions.requestToOpenWindow({});
          }
          if (
            windowState == ChatWindowStates.OPENED ||
            windowState == ChatWindowStates.MINIMIZED
          ) {
            // End the active session & open a new one with the context
            return WindowStateActions.endSessionToStartNew({
              pendingAction: {
                type: PendingActionType.startNewSession,
                parameters: urlParams.vaJourney,
              },
            });
          }
          return WindowStateActions.requestToOpenWindow({
            context: urlParams.vaJourney,
          });
        } else {
          // Check for app refresh
          return WindowMessagesActions.checkAppRefresh();
        }
      })
    )
  );

  checkForAppRefresh$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WindowMessagesActions.checkAppRefresh),
      concatLatestFrom(() => this.store.select(selectWindowStateState)),
      filter(
        ([, windowState]) =>
          windowState == ChatWindowStates.OPENED ||
          windowState == ChatWindowStates.MINIMIZED
      ),
      switchMap(([, windowState]) =>
        of(WindowMessagesActions.onAppRefresh({ windowState }))
      )
    )
  );

  sendFloatingBtnMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WindowMessagesActions.toggleFloatingBtn),
      switchMap((action) => {
        const isFloatingButtonEnabled = this.includes(
          action.pageList,
          window.parent.location.pathname.toLowerCase().replace(/\/$/, '')
        );
        return of(
          VaButtonActions.enableOrDisableFloatingButton({
            isFloatingButtonEnabled,
          })
        );
      })
    )
  );

  sendAppRefreshMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WindowMessagesActions.onAppRefresh),
      concatLatestFrom(() => this.store.select(selectPendingActionState)),
      filter(([, pendingActionState]) => !!pendingActionState.type),
      map(() => PendingActionActions.handlePendingActionAfterRefresh())
    )
  );

  sendMinimizeMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WindowStateActions.requestToMinimizeWindow),
      concatLatestFrom(() => [
        this.store.select(selectReceivedFirstOperatorMessage),
        this.store.select(selectOperatorName),
      ]),
      switchMap(([, isLiveChat, agentName]) =>
        of(VaButtonActions.minimize({ isLiveChat, agentName }))
      )
    )
  );

  sendWindowMessage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(WindowMessagesActions.sendWindowMessage),
        map((action) => {
          window?.parent?.postMessage(
            {
              message: action.message,
              messageData: action.messageData,
            },
            window.location.origin
          );
        })
      ),
    { dispatch: false }
  );

  includes(pageList: string[], value: string): boolean {
    value = value.toLowerCase();
    const container = pageList.map((item) => item.toLowerCase());
    const generalPaths = container.filter((item) => item.includes('/*'));
    if (generalPaths?.length) {
      for (const path of generalPaths) {
        // For example path = '/consumer/self-serve/*',
        const slicedPath = path.slice(0, -2); // Remove the last '/*'
        // For example
        // value = '/consumer/self-serve/link-account/postpaid
        // slicedPath = '/consumer/self-serve'
        if (value.includes(slicedPath)) {
          return true;
        }
      }
    }
    return container.indexOf(value) >= 0;
  }
}
